import { create } from 'zustand';
import { persist } from 'zustand/middleware';
export { canEnterAdministration } from '../utils/administrationAccess';

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'customer' | 'agent' | 'admin';
  recovery_scope?: boolean;
  customerId?: string;
  mustChangePassword?: boolean;
  is_admin?: boolean;
  isAdmin?: boolean;
  permissions?: string[];
  securityGroup?: string | null;
  /** Console modules this user may access (from /auth/me). undefined = unrestricted (show all). */
  allowedModules?: string[];
  themePreset?: string | null;
  themeMode?: 'light' | 'dark' | 'system' | null;
}

/**
 * Whether a user may access a console module. Fail-open: admins and users without an
 * allowedModules list (undefined/empty) see every module — never hides the whole nav.
 */
export const canAccessModule = (user: User | null, moduleId: string): boolean => {
  if (!user) return true;
  if (user.role === 'admin' || user.is_admin || user.isAdmin) return true;
  const mods = user.allowedModules;
  if (!mods || mods.length === 0) return true;
  return mods.includes(moduleId);
};

export const hasAdminPermission = (user: User | null, permission?: string): boolean => {
  if (!user) return false;
  if (user.recovery_scope) return permission === 'maintenance:recovery';
  const isFullAdmin = user.role === 'admin' || user.is_admin || user.isAdmin;
  const perms = user.permissions || [];
  
  if (isFullAdmin && (perms.length === 0 || perms.includes('*'))) {
    return true;
  }
  
  if (permission) {
    return perms.includes(permission);
  }
  
  return isFullAdmin || perms.some(p => p.startsWith('admin:'));
};

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  mustChangePassword: boolean;
  
  // Actions
  setUser: (user: User | null) => void;
  setTokens: (accessToken?: string, refreshToken?: string) => void;
  loginSuccess: (user: User, accessToken: string, refreshToken: string) => void;
  setMustChangePassword: (v: boolean) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      mustChangePassword: false,

      setUser: (user) => set({ 
        user, 
        isAuthenticated: !!user 
      }),

      setTokens: (accessToken, refreshToken) => set({ 
        accessToken: accessToken || null,
        refreshToken: refreshToken || null,
        isAuthenticated: true
      }),

      loginSuccess: (user, accessToken, refreshToken) => set({
        user,
        accessToken: accessToken || null,
        refreshToken: refreshToken || null,
        isAuthenticated: true
      }),

      setMustChangePassword: (mustChangePassword) => set({ mustChangePassword }),

      logout: () => set({ 
        user: null, 
        accessToken: null, 
        refreshToken: null, 
        isAuthenticated: false,
        mustChangePassword: false,
        error: null 
      }),

      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
    }),
    {
      name: 'ticket-auth-storage',
      partialize: (state) => ({ 
        user: state.user, 
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated 
      }),
    }
  )
);

export default useAuthStore;
